import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig } from './auth/authConfig'
import AuthProvider from './auth/AuthProvider'
import ErrorBoundary from './components/ErrorBoundary'
import './index.css'
import App from './App.tsx'

// CRITICAL: Instantiate MSAL at module level, outside the React component tree.
// This is required by @azure/msal-react for correct initialization.
const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(async () => {
  // Process auth code from redirect flow — after Microsoft login,
  // the browser returns here with #code= in the URL hash.
  // This must complete before rendering so the account is available.
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      msalInstance.setActiveAccount(response.account);
    }
  } catch (err) {
    // Redirect promise can fail if auth code is invalid/expired — log but continue
    console.warn('[MSAL] handleRedirectPromise failed:', err);
  }

  // Set active account from cache if available (e.g., returning user)
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <AuthProvider msalInstance={msalInstance}>
          <App />
        </AuthProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}).catch((err) => {
  // If MSAL fails to initialize, show error in the DOM so users see something
  console.error('[MSAL] Initialization failed:', err);
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui;padding:2rem;">
        <div style="text-align:center;max-width:400px;">
          <h1 style="color:#2E5C8A;margin-bottom:1rem;">Harmony AP</h1>
          <p style="color:#666;margin-bottom:1rem;">Authentication failed to initialize. Please try refreshing the page.</p>
          <p style="color:#999;font-size:0.85rem;">${err instanceof Error ? err.message : String(err)}</p>
          <button onclick="location.reload()" style="margin-top:1rem;padding:0.5rem 1.5rem;background:#2E5C8A;color:white;border:none;border-radius:4px;cursor:pointer;">Refresh</button>
        </div>
      </div>`;
  }
});

// Register service worker in production only
if (import.meta.env.PROD && navigator.serviceWorker) {
  navigator.serviceWorker.register('/sw.js')
    .then((registration) => {
      console.log('SW registered:', registration.scope);

      // Detect service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          // New SW installed and there's an existing controller = this is an UPDATE
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            const shouldUpdate = confirm(
              'A new version of Harmony AP is available. Reload to update?'
            );
            if (shouldUpdate) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          }
        });
      });
    })
    .catch((err) => console.warn('SW registration failed:', err));

  // Reload when the new service worker takes control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}
